import { AgGridCellValueMethod } from '@/enums/axiom-report.enum'
import {
  ICashflowReport,
  IConstraintReport,
  ISummaryReport,
  ITransactionReports,
} from '@/interfaces/portfolioOptimization.interface'
import axiosInstance from '@/utils/interceptor'
import { formatNumber, removeCommaFromString } from '@/utils/utils'

/**
 * Get Transaction Reports
 * @returns Promise<ITransactionReports[]> list of transaction reports
 */
export const getTransactionReports = async (
  id: number,
): Promise<ITransactionReports[]> => {
  const response = await axiosInstance.get(`transactions?reportId=${id}`)
  return response.data as ITransactionReports[]
}

/**
 * Get Constraint Reports
 * @returns Promise<IConstraintReport[]> list of constraint reports
 */
export const getConstraintReport = async (
  id: number,
): Promise<IConstraintReport> => {
  const response = await axiosInstance.get(
    `optimization/constraints-report?reportId=${id}`,
  )
  return response.data as IConstraintReport
}

/**
 * Get Cashflow Reports
 * @returns Promise<ICashflowReport[]> list of cashflow reports
 */
export const getCashflowReport = async (
  id: number,
): Promise<ICashflowReport[]> => {
  const response = await axiosInstance.get(
    `optimization/cashflow-report?reportId=${id}`,
  )
  return response.data as ICashflowReport[]
}

/**
 * Get Summary Reports
 * @returns Promise<ISummaryReport[]> list of summary reports
 */
export const getSummaryReport = async (id: number): Promise<ISummaryReport> => {
  const response = await axiosInstance.get(
    `optimization/summary-report?reportId=${id}`,
  )
  return response.data as ISummaryReport
}

export const getTransformSummaryData = (summaryReportData: ISummaryReport) => {
  const formattedData = Object.fromEntries(
    Object.entries(summaryReportData).map(([key, value]) => {
      if (typeof value === 'number') {
        const fractionDigit = 2
        return [key, formatNumber(value, false, fractionDigit, fractionDigit)]
      }
      return [key, value]
    }),
  )

  // Helper function to handle null before values
  const getBeforeValue = (beforeValue: any) => {
    if (beforeValue === null) {
      return 'N/A'
    }
    return beforeValue
  }

  return [
    {
      title: 'Optimization Parameters',
      data: [
        { label: 'Method', value: formattedData.method },
        { label: 'Objective Function', value: formattedData.obj_func },
        { label: 'Objective Value', value: formattedData.obj_val },
      ],
    },
    {
      title: 'Transactions',
      data: [
        { label: 'Bought', value: formattedData.bought },
        { label: 'Sold', value: formattedData.sold },
        { label: 'Held', value: formattedData.held },
        { label: 'Net Bought', value: formattedData.net_bought },
        { label: 'Total Transacted', value: formattedData.total_trans },
        { label: 'Transaction Cost', value: formattedData.trans_cost },
        { label: 'Turnover Level', value: formattedData.turnover_level },
      ],
    },
    {
      title: 'Solution Portfolio Value',
      data: [
        { label: 'Market Value of Par', value: formattedData.mktv_par },
        { label: 'Accrued Income', value: formattedData.accrue },
        { label: 'Cash', value: formattedData.cash },
        { label: 'Total Market Value', value: formattedData.total_mktv },
        { label: 'Total Par Value', value: formattedData.total_par_val },
      ],
    },
    {
      title: 'Solution Portfolio Statistics',
      data: [
        { 
          label: 'Effective Duration - Level', 
          before: getBeforeValue(formattedData.edur_before),
          after: formattedData.edur
        },
        { 
          label: 'Effective D2 - Slope', 
          before: getBeforeValue(formattedData.ed2_before),
          after: formattedData.ed2
        },
        { 
          label: 'Effective D3 - Curvature', 
          before: getBeforeValue(formattedData.ed3_before),
          after: formattedData.ed3
        },
        { 
          label: 'Cash Flow Yield', 
          before: getBeforeValue(formattedData.cf_yld_before),
          after: formattedData.cf_yld
        },
        { 
          label: 'Current Yield', 
          before: getBeforeValue(formattedData.curr_yld_before),
          after: formattedData.curr_yld
        },
        { 
          label: 'Coupon Rate', 
          before: getBeforeValue(formattedData.avg_coupon_before),
          after: formattedData.avg_coupon
        },
        { 
          label: 'Avg Price', 
          before: getBeforeValue(formattedData.avg_price_before),
          after: formattedData.avg_price
        },
        { 
          label: 'Years to Maturity', 
          before: getBeforeValue(formattedData.yrs_to_matu_before),
          after: formattedData.yrs_to_matu
        },
        { 
          label: 'Turnover', 
          before: 'N/A',
          after: formattedData.turnover
        },
        { 
          label: 'Total Market Value', 
          before: formattedData.start_val,
          after: formattedData.end_val
        }
      ],
    },
  ]
}

export const getUpdatedCellValue = (
  rowData: any,
  field: string,
  type: string,
) => {
  if (rowData) {
    if (rowData[field] || typeof rowData[field] === 'number') {
      let value: number | string
      const fractionDigit = 2
      if (field === 'par') {
        value = formatNumber(Math.round(rowData[field]), false, 0, 0)
      } else {
        value = formatNumber(
          rowData[field],
          false,
          fractionDigit,
          fractionDigit,
        )
      }
      if (
        value &&
        String(value).includes(',') &&
        type === AgGridCellValueMethod.VALUE_FORMATTER
      ) {
        return value
      }
      if (
        value &&
        type === AgGridCellValueMethod.VALUE_FORMATTER &&
        field !== 'par'
      ) {
        value = removeCommaFromString(String(value))
        return parseFloat(value).toFixed(fractionDigit)
      }
      value = removeCommaFromString(String(value))
      return Number(value)
    }
    return rowData[field]
  }
  return rowData
}
